import { SmartToy } from '@mui/icons-material';
import { Box, Fab, TextField, Dialog, DialogContent, DialogTitle, IconButton, Typography, Paper } from '@mui/material';
import { Close, Send } from '@mui/icons-material';
import { useChatAgent } from '@src/App/Components/Agents/hooks/useChatAgent';
import { EAgentModule } from '@sharedInterfaces/ChatAgent';
import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';

interface OnboardingChatButtonProps {
    employeeTalkId: number;
    currentStep: number;
    stepName: string;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

export const OnboardingChatButton: React.FC<OnboardingChatButtonProps> = ({
    employeeTalkId,
    currentStep,
    stepName,
    open: controlledOpen,
    onOpenChange,
}) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
    const setOpen = onOpenChange || setInternalOpen;
    const [input, setInput] = useState('');
    const { messages, isTyping, sendMessage } = useChatAgent({ autoInit: false });
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = () => {
        if (!input.trim() || isTyping) return;
        
        sendMessage(input, {
            module: EAgentModule.ONBOARDING,
            subContext: {
                employeeTalkId,
                currentStep,
                stepName,
            },
        });
        
        setInput('');
    };

    return (
        <>
            <Fab
                color="primary"
                sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000 }}
                onClick={() => setOpen(true)}
            >
                <SmartToy />
            </Fab>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SmartToy color="primary" />
                            <Typography variant="h6">Onboarding Assistent</Typography>
                        </Box>
                        <IconButton onClick={() => setOpen(false)} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        Schritt {currentStep + 1}: {stepName}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ height: 400, overflowY: 'auto', mb: 2, p: 1 }}>
                        {messages.map((msg, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    mb: 2,
                                    display: 'flex',
                                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                }}
                            >
                                <Paper
                                    elevation={1}
                                    sx={{
                                        p: 1.5,
                                        maxWidth: '80%',
                                        bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.100',
                                        color: msg.role === 'user' ? 'white' : 'text.primary',
                                        borderRadius: 2,
                                        '& h3': { fontSize: '1rem', fontWeight: 600, mt: 1, mb: 0.5 },
                                        '& ul, & ol': { pl: 2, my: 0.5 },
                                        '& li': { mb: 0.25 },
                                        '& p': { my: 0.5 },
                                        '& strong': { fontWeight: 600 },
                                        '& code': { 
                                            bgcolor: msg.role === 'user' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)',
                                            px: 0.5,
                                            py: 0.25,
                                            borderRadius: 0.5,
                                            fontSize: '0.875em'
                                        },
                                    }}
                                >
                                    {msg.role === 'user' ? (
                                        <Typography variant="body2">{msg.text}</Typography>
                                    ) : (
                                        <ReactMarkdown
                                            components={{
                                                p: ({ children }) => <Typography variant="body2" component="span">{children}</Typography>,
                                                h3: ({ children }) => <Typography variant="subtitle1" component="h3">{children}</Typography>,
                                                li: ({ children }) => <Typography variant="body2" component="li">{children}</Typography>,
                                            }}
                                        >
                                            {msg.text}
                                        </ReactMarkdown>
                                    )}
                                </Paper>
                            </Box>
                        ))}
                        {isTyping && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                                <Paper elevation={1} sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Assistent tippt...
                                    </Typography>
                                </Paper>
                            </Box>
                        )}
                        <div ref={messagesEndRef} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                            placeholder="Frage zum Onboarding..."
                            disabled={isTyping}
                        />
                        <IconButton onClick={handleSend} disabled={!input.trim() || isTyping} color="primary">
                            <Send />
                        </IconButton>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};
